/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable react/display-name */
import { ICache } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { ICommerceApiSettings, IExperimentationConnector, IGeoLocationConnector, ISegmentationConnectors } from './contract';
import {
    ComponentType,
    IComponent,
    IComponentProps,
    IDictionary,
    IFeatureVersions,
    ILocalCredentials,
    IMergedAppSettings,
    IMSDyn365Window,
    IPartnerImageSettings,
    IPartnerThemeSettings,
    IPlatformSettings,
    IResourceManager,
    ISecretManager,
    IThemeModule,
    IThemeRegistration,
    ISiteCssUrl
} from './interfaces';

declare let window: IMSDyn365Window;

/**
 * App level const available to module/action developer
 */
export class MSDyn365Commerce {
    private _cartCookie: string = '_msdyn365___cart_';
    private _checkoutCartCookie: string = '_msdyn365___checkout_cart_';
    private _checkoutCartKeyPrefix: string = 'checkout';
    private _buildVersion: string = '00-0000';
    private _env: string = 'development';
    private _commerceApiSetting: ICommerceApiSettings = <ICommerceApiSettings>{};
    private _themeRegistration: IThemeRegistration = { themes: {}, styles: {} };
    private _resourceManager: IResourceManager | undefined;
    private _imageSettings: IPartnerImageSettings = { imageSettings: {}, gridSettings: {} };
    private _themeSettings: IDictionary<IPartnerThemeSettings> = {};
    private _themeModules: IThemeModule[] = [];
    private _connectorCache: ICache | undefined;
    private _secretManager: ISecretManager | undefined;
    private _experimentationConnector: IExperimentationConnector | undefined;
    private _geoLookupConnector: IGeoLocationConnector | undefined;
    private _platformSettings: IPlatformSettings | undefined;
    private _segmentationConnectors: ISegmentationConnectors | undefined;
    private _initialLoadCompleted: boolean = false;
    private _localCredentials: ILocalCredentials | undefined;
    private _renderResultsCache: ICache | undefined;
    private _environmentVariable: IDictionary<string> = {};
    private _siteCssUrl: ISiteCssUrl | undefined;
    private _isClientHydrate: boolean = false;
    private _appSettings: IMergedAppSettings = {
        app: { config: {}, routes: {} },
        msdyn365: { config: {}, routes: {}, contentSecurityPolicy: {} }
    };
    private _featureNames: string[] = [];

    private _versionDependentFeatureInfo: IFeatureVersions = {};
    private _moduleCssStaticPaths: Set<string> = new Set();

    public getEnvironmentVariable = (environmentName: string) => {
        let defaultValue = '';
        switch (environmentName) {
            case 'SUBMISSIONID':
                defaultValue = '00000-00000-00000-00000-00000';
                break;
            default:
        }
        return this._environmentVariable[environmentName] || defaultValue;
    };

    public setClientHydrate = (isClientHydrate: boolean) => {
        this._isClientHydrate = isClientHydrate;
    };

    public getAllEnvironmentVariable = () => {
        return this._environmentVariable;
    };

    public setEnvironmentVariable = () => {
        this._environmentVariable['SUBMISSIONID'] = process.env.SUBMISSIONID || '';
    };

    public setInitialLoadCompleted = (initialLoadCompleted: boolean) => {
        this._initialLoadCompleted = initialLoadCompleted;
    };
    public setThemeRegistration = (themeRegistration: IThemeRegistration) => {
        this._themeRegistration = themeRegistration;
    };

    /**
     * Sets list of theme module objects
     */
    public setThemeModules = (themeModules: IThemeModule[]) => {
        this._themeModules = themeModules;
    };

    /**
     * Sets list of theme module objects
     */
    public createComponent = <T extends IComponent<IComponentProps>>(name: ComponentType, componentObj: T): React.FC<IComponentProps> => (
        props: IComponentProps
    ) => {
        if (!(componentObj && componentObj.component)) {
            throw new Error(`Component ${name} missing`);
        }

        const Element = componentObj.component;
        return React.createElement(Element, props);
    };

    /**
     * Creates a component override
     */
    public createComponentOverride = <T extends IComponent<IComponentProps>>(
        name: ComponentType,
        componentObj: T
    ): React.FC<IComponentProps> => (props: IComponentProps) => {
        if (!(componentObj && componentObj.component)) {
            throw new Error(`Component ${name} missing`);
        }

        const Element = componentObj.component;
        return React.createElement(Element, props);
    };

    get isClientHydrate() {
        return this._isClientHydrate;
    }

    get siteCssUrl(): ISiteCssUrl | undefined {
        return this._siteCssUrl;
    }

    /**
     * Check if its in postInitialLoading status
     */

    get initialLoadCompleted(): boolean {
        return this._initialLoadCompleted;
    }

    // DO NOT DELETE - keeping this method for backwards
    // compatibility reasons. After modules code tranisitions to using cartCookieName
    // we will delete this getter
    get CartCookie(): string {
        return this._cartCookie;
    }

    get cartCookieName(): string {
        return this._cartCookie;
    }

    /**
     * Get checkout cart cookie
     */
    get checkoutCartCookie(): string {
        return this._checkoutCartCookie;
    }

    /**
     * Get checkout cart cookie
     */
    get checkoutCartKeyPrefix(): string {
        return this._checkoutCartKeyPrefix;
    }

    get themes(): IThemeRegistration {
        return this._themeRegistration;
    }

    /**
     * Gets theme modules with full layouts and segment objects
     */
    get themeModules(): IThemeModule[] {
        return this._themeModules;
    }
    /**
     * Gets the current build version
     */
    get buildVersion(): string {
        return this._buildVersion;
    }

    /**
     * Gets the current runtime environment
     */
    get env(): string {
        return this._env;
    }

    /**
     * Gets the commerce related API settings
     */
    get apiSettings(): ICommerceApiSettings {
        return this._commerceApiSetting;
    }

    /**
     * Gets the commerce related image settings
     */
    get imageSettings(): IPartnerImageSettings {
        return this._imageSettings;
    }

    /**
     * Gets the commerce related theme settings
     */
    get themeSettings(): IDictionary<IPartnerThemeSettings> {
        return this._themeSettings;
    }

    /**
     * Gets the commerce related APP settings
     */
    get appSettings(): IMergedAppSettings {
        return this._appSettings;
    }

    /**
     * Gets the commerce related APP settings
     */
    get featureNames(): string[] {
        return this._featureNames;
    }

    /**
     * Gets the experimentation connector object that contains the registered provider, listener and connector name
     * Returns undefined if an experimentation connector has not been registered
     */
    get experimentationConnector(): IExperimentationConnector | undefined {
        return this._experimentationConnector;
    }

    /**
     * Gets the all segmentation connectors that contains the registered provider and connector name
     * Returns undefined if a segmentation connector has not been registered
     */
    get segmentationConnectors(): ISegmentationConnectors | undefined {
        return this._segmentationConnectors;
    }

    /**
     * Gets the geoLookup connector object that contains the registered provider and connector name
     * Returns undefined if a geo lookup connector has not been registered
     */
    get geoLookupConnector(): IGeoLocationConnector | undefined {
        return this._geoLookupConnector;
    }

    /**
     * Gets the experimentation app cache
     */
    get connectorCache(): ICache | undefined {
        return this._connectorCache;
    }

    /**
     * Gets secret manager object
     */
    get secretManager(): ISecretManager | undefined {
        return this._secretManager;
    }

    /**
     * Gets local credential details
     */
    get localCredentials(): ILocalCredentials | undefined {
        return this._localCredentials;
    }

    /**
     * Gets the platformSettings
     */

    get platformSettings(): IPlatformSettings | undefined {
        return this._platformSettings;
    }

    /**
     * Returns a boolean value indicating whether or not the current operating environment is browser or server
     */
    get isBrowser(): boolean {
        return typeof window !== 'undefined';
    }

    /**
     * Gets the resource manager instance
     */
    get resourceManager(): IResourceManager | undefined {
        if (!this.isBrowser) {
            return this._resourceManager;
        }

        return undefined;
    }

    /**
     * Gets the page render result cache
     */
    get renderResultCache(): ICache | undefined {
        return this._renderResultsCache;
    }

    /**
     * Gets the version dependent feature info
     */
    get getVersionDependentFeatureInfo(): IFeatureVersions {
        return this._versionDependentFeatureInfo;
    }

    /**
     * Gets chunks paths of all moduleCSS styles
     */
    get allModuleCssStaticPaths(): Set<String> {
        return this._moduleCssStaticPaths;
    }
}

const msdyn365Commerce = new MSDyn365Commerce();
export default msdyn365Commerce;
